<template>
  <div
    class="base-leaflet-map sarpa-orange-theme"
    :style="{ height: height }"
    ref="mapWrapper"
  >
    <!-- Loading Skeleton -->
    <transition name="skeleton-fade">
      <div v-if="loading" class="map-skeleton">
        <div class="skeleton-shimmer"></div>
        <div class="skeleton-label">
          <v-icon color="#9ca3af" size="28">mdi-map-outline</v-icon>
          <span>Loading map…</span>
        </div>
      </div>
    </transition>

    <!-- Leaflet map container -->
    <div ref="mapContainer" class="map-leaflet-container"></div>

    <!-- ── Floating UI Panels ─────────────────────────────────────────────── -->

    <!-- Top toolbar (title + search) -->
    <MapToolbar
      :title="title"
      :config="toolbar"
      :search-manager="searchMgr"
      @search-result="onSearchResult"
    />

    <!-- Right filter panel -->
    <MapFilterPanel
      :layers="layersWithVisibility"
      @layer-toggle="onLayerToggle"
      @display-mode-change="onDisplayModeChange"
    >
      <slot name="custom-filters"></slot>
    </MapFilterPanel>

    <!-- Map controls (zoom, fit, satellite, fullscreen) -->
    <MapControls
      :config="toolbar"
      @zoom-in="zoomIn"
      @zoom-out="zoomOut"
      @fit-bounds="onFitBounds"
      @tile-change="onTileChange"
    />

    <!-- Bottom statistics bar -->
    <MapStatistics :stats="statistics" />
  </div>
</template>

<script>
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';

import MapToolbar     from './panels/MapToolbar.vue';
import MapFilterPanel from './panels/MapFilterPanel.vue';
import MapControls    from './panels/MapControls.vue';
import MapStatistics  from './panels/MapStatistics.vue';

import { createLeaflet }        from './composables/useLeaflet';
import { createLayerManager }   from './composables/useLayers';
import { createFitBoundsManager } from './composables/useFitBounds';
import { createSearchManager }  from './composables/useSearch';
import { createPopupManager }   from './composables/usePopups';

export default {
  name: 'BaseLeafletMap',

  components: {
    MapToolbar,
    MapFilterPanel,
    MapControls,
    MapStatistics,
  },

  props: {
    /** Array of layer config objects produced by layerFactory() */
    layers: {
      type:    Array,
      default: () => [],
    },
    /** Config-driven statistics cards — [{ title, value, icon, color }] */
    statistics: {
      type:    Array,
      default: () => [],
    },
    /** Toolbar feature flags */
    toolbar: {
      type:    Object,
      default: () => ({
        search:     true,
        fullscreen: true,
        fitBounds:  true,
        satellite:  true,
      }),
    },
    /** Optional map title displayed in top-left badge */
    title: {
      type:    String,
      default: '',
    },
    /** CSS height string for the map container */
    height: {
      type:    String,
      default: '650px',
    },
    /** Show loading skeleton overlay */
    loading: {
      type:    Boolean,
      default: false,
    },
  },

  data() {
    return {
      // Internal composable instances
      leafletCtrl:    null,
      layerMgr:       null,
      fitBoundsMgr:   null,
      searchMgr:      null,
      popupMgr:       null,

      // Map-level visibility state (separate from layers prop — never mutate props)
      layerVisibility: {},
    };
  },

  computed: {
    /** Layers merged with internal visibility state for filter panel & legend */
    layersWithVisibility() {
      return this.layers.map(l => ({
        ...l,
        visible: this.layerVisibility[l.id] !== undefined
          ? this.layerVisibility[l.id]
          : l.visible !== false,
      }));
    },

    /** Flat legend items derived from currently visible layers */
    computedLegend() {
      return this.layersWithVisibility
        .filter(l => l.visible && l.legend)
        .flatMap(l => l.legend || []);
    },
  },

  watch: {
    layers: {
      deep: true,
      handler(newLayers) {
        if (!this.layerMgr) return;

        // Sync new layers with internal visibility state
        newLayers.forEach(l => {
          if (!(l.id in this.layerVisibility)) {
            this.$set(this.layerVisibility, l.id, l.visible !== false);
          }
        });

        // Apply visibility overrides from local state
        const layersToUpdate = newLayers.map(l => ({
          ...l,
          visible: this.layerVisibility[l.id] !== undefined
            ? this.layerVisibility[l.id]
            : l.visible !== false,
        }));

        this.layerMgr.updateLayers(layersToUpdate);
        this.searchMgr.buildIndex(newLayers);

        // Auto fitBounds removed to keep full map view by default
        // setTimeout(() => {
        //   if (this.layerMgr && this.fitBoundsMgr) {
        //     this.fitBoundsMgr.fit(this.layerMgr.getAllVisibleLatLngs());
        //   }
        // }, 400);
      },
    },
  },

  mounted() {
    this.$nextTick(() => {
      this.initMap();
    });
  },

  beforeDestroy() {
    if (this.popupMgr)   this.popupMgr.destroyAll();
    if (this.layerMgr)   this.layerMgr.destroy();
    if (this.leafletCtrl) this.leafletCtrl.destroy();
  },

  methods: {
    initMap() {
      const container = this.$refs.mapContainer;
      if (!container) return;

      // ── Popup manager (needs router + store) ──────────────────────────────
      this.popupMgr = createPopupManager(this.$router, this.$store);

      // ── Leaflet engine ────────────────────────────────────────────────────
      this.leafletCtrl = createLeaflet(container, {}, (event, payload) => {
        this.$emit(event, payload);
      });
      const map = this.leafletCtrl.map;

      // ── Layer manager ──────────────────────────────────────────────────────
      this.layerMgr = createLayerManager(map, this.popupMgr, (event, payload) => {
        this.$emit(event, payload);
      });

      // ── Fit bounds manager ─────────────────────────────────────────────────
      this.fitBoundsMgr = createFitBoundsManager(map);

      // ── Search manager ─────────────────────────────────────────────────────
      this.searchMgr = createSearchManager();

      // ── Init layer visibility state ────────────────────────────────────────
      this.layers.forEach(l => {
        this.$set(this.layerVisibility, l.id, l.visible !== false);
      });

      // ── Load initial layers ────────────────────────────────────────────────
      if (this.layers.length) {
        this.layerMgr.updateLayers(this.layersWithVisibility);
        this.searchMgr.buildIndex(this.layers);

        // Auto fitBounds removed to keep full map view by default
        // setTimeout(() => {
        //   if (this.layerMgr && this.fitBoundsMgr) {
        //     this.fitBoundsMgr.fit(this.layerMgr.getAllVisibleLatLngs());
        //   }
        // }, 600);
      }

      this.$emit('map-ready', { map });
    },

    // ── Panel event handlers ────────────────────────────────────────────────

    onLayerToggle({ layerId, visible }) {
      this.$set(this.layerVisibility, layerId, visible);
      if (this.layerMgr) this.layerMgr.toggle(layerId, visible);
    },

    onDisplayModeChange(mode) {
      if (this.layerMgr) {
        this.layerMgr.setDisplayMode(mode, this.layersWithVisibility);
      }
    },

    onSearchResult(result) {
      if (!this.leafletCtrl || !result) return;
      const { map } = this.leafletCtrl;
      map.setView([result.lat, result.lng], 14, { animate: true });
    },

    onFitBounds() {
      if (this.fitBoundsMgr && this.layerMgr) {
        this.fitBoundsMgr.fit(this.layerMgr.getAllVisibleLatLngs());
      }
    },

    onTileChange(providerId) {
      if (this.leafletCtrl) this.leafletCtrl.switchTile(providerId);
    },

    zoomIn() {
      if (this.leafletCtrl) this.leafletCtrl.map.zoomIn();
    },

    zoomOut() {
      if (this.leafletCtrl) this.leafletCtrl.map.zoomOut();
    },
  },
};
</script>

<style scoped>
/* ── Wrapper ────────────────────────────────────────────────────────────────── */
.base-leaflet-map {
  position: relative;
  width: 100%;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.10);
}

/* ── Leaflet container fills the wrapper ─────────────────────────────────────── */
.map-leaflet-container {
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  z-index: 0;
}

/* ── Loading skeleton ────────────────────────────────────────────────────────── */
.map-skeleton {
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  z-index: 2000;
  background: #fdfbf7;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 16px;
}

.skeleton-shimmer {
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  background: linear-gradient(
    90deg,
    rgba(255,255,255,0)   0%,
    rgba(255,255,255,0.6) 50%,
    rgba(255,255,255,0)   100%
  );
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

@keyframes shimmer {
  0%   { background-position: -200% 0; }
  100% { background-position:  200% 0; }
}

.skeleton-label {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  z-index: 1;
  font-family: poppinsregular, 'Poppins', sans-serif;
  font-size: 13px;
  color: #9ca3af;
}

/* ── Skeleton transition ─────────────────────────────────────────────────────── */
.skeleton-fade-leave-active { transition: opacity 0.4s ease; }
.skeleton-fade-leave-to     { opacity: 0; }
</style>

<style>
/* Global: override Leaflet popup chrome for all sarpa popups */
.sarpa-leaflet-popup .leaflet-popup-content-wrapper {
  padding: 0 !important;
  border-radius: 14px !important;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.16) !important;
  overflow: hidden !important;
  border: none !important;
}
.sarpa-leaflet-popup .leaflet-popup-content {
  margin: 0 !important;
  line-height: 1 !important;
}
.sarpa-leaflet-popup .leaflet-popup-tip-container {
  display: none !important;
}
.sarpa-leaflet-popup .leaflet-popup-close-button {
  top: 10px !important;
  right: 10px !important;
  color: rgba(255, 255, 255, 0.9) !important;
  font-size: 18px !important;
}

/* Global popup structural styles */
.sarpa-popup {
  font-family: poppinsregular, 'Poppins', sans-serif;
  border-radius: 14px;
  overflow: hidden;
  background: #ffffff;
  min-width: 240px;
  max-width: 300px;
}
.popup-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
}
.popup-header-icon {
  width: 34px;
  height: 34px;
  background: rgba(255,255,255,0.22);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.popup-entity-name {
  font-family: poppinsmedium, 'Poppins', sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: #fff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 170px;
}
.popup-entity-sub {
  font-size: 10px;
  color: rgba(255,255,255,0.75);
  margin-top: 1px;
}
.popup-body {
  padding: 10px 14px;
  display: flex;
  flex-direction: column;
  gap: 7px;
}
.popup-row {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12.5px;
  color: #374151;
}
.popup-row svg { flex-shrink: 0; }
.popup-actions {
  padding: 8px 14px 12px;
  display: flex;
  gap: 8px;
}
.popup-btn {
  flex: 1;
  padding: 7px 12px;
  border: none;
  border-radius: 8px;
  font-family: poppinsmedium, 'Poppins', sans-serif;
  font-size: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  transition: opacity 0.15s;
}
.popup-btn:hover { opacity: 0.85; }
.popup-btn.primary {
  background: linear-gradient(135deg, #fb8c00 0%, #f57c00 100%);
  color: #fff;
}
.popup-btn.secondary {
  background: #f3f4f6;
  color: #374151;
}

/* Custom marker — remove default Leaflet blue marker styles */
.sarpa-map-marker {
  background: none !important;
  border: none !important;
}
.sarpa-map-marker svg {
  transition: transform 0.15s ease;
  will-change: transform;
}
.sarpa-map-marker:hover svg {
  transform: scale(1.12) translateY(-2px);
}

/* Hide default leaflet marker shadows that cause black blobs */
.leaflet-marker-shadow {
  display: none !important;
}

/* Remove default cluster styles (we use fully custom ones) */
.sarpa-cluster .sarpa-cluster-bubble {
  transition: transform 0.15s;
}
.sarpa-cluster:hover .sarpa-cluster-bubble {
  transform: scale(1.08);
}

/* Light orange theme for map tiles */
.sarpa-orange-theme .leaflet-tile-pane {
  filter: brightness(0.95) sepia(0.15) hue-rotate(-20deg) contrast(1.02);
}
</style>
